import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  Download,
  Mail,
  XCircle,
  RefreshCw,
  Search,
  Layout,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  ShieldCheck,
  Eye,
  Filter,
  CheckCircle2,
  Clock,
  Link2,
  FileCheck,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  StatCard,
  Input,
  SearchInput,
} from '@azyntor-labs/nexus-ui';
import { certificatesApi, examsApi } from '@/api/endpoints';
import type { Certificate, CertificateTemplate } from '@/api/endpoints';
import { Exam } from '@/types';
import { toast } from '@/utils/toast';

export const CertificateManagementPage = () => {
  const queryClient = useQueryClient();
  const [selectedExamId, setSelectedExamId] = useState('');
  const [activeTab, setActiveTab] = useState<'templates' | 'certificates'>('templates');
  const [searchTerm, setSearchTerm] = useState('');
  const [examSearchTerm, setExamSearchTerm] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [certPage, setCertPage] = useState(1);
  const [sendingAllEmails, setSendingAllEmails] = useState(false);

  // Fetch exams
  const { data: examsRes } = useQuery({
    queryKey: ['exams-for-certificates'],
    queryFn: () => examsApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  // Fetch certificate stats
  const { data: statsRes } = useQuery({
    queryKey: ['certchain-stats'],
    queryFn: () => certificatesApi.getStats(),
    staleTime: 60000,
  });

  const certStats = statsRes?.data?.data || {};

  const exams: Exam[] = Array.isArray(examsRes?.data?.data?.data)
    ? examsRes.data.data.data
    : Array.isArray(examsRes?.data?.data)
      ? examsRes.data.data
      : [];

  // Fetch certchain templates
  const { data: templatesRes, isLoading: templatesLoading } = useQuery({
    queryKey: ['certchain-templates'],
    queryFn: () => certificatesApi.getTemplates({ type: 'exam' }),
    staleTime: 60000,
  });

  const templates: CertificateTemplate[] = templatesRes?.data?.data || [];

  // Fetch certificates for selected exam (via certchain)
  const { data: certsRes, isLoading: certsLoading } = useQuery({
    queryKey: ['certchain-certificates', selectedExamId, certPage],
    queryFn: () => certificatesApi.list({ source_type: 'exam', source_id: selectedExamId, page: certPage, per_page: 20 }),
    enabled: !!selectedExamId && activeTab === 'certificates',
    staleTime: 15000,
  });

  const certificates: Certificate[] = certsRes?.data?.data || [];
  const certTotalPages = certsRes?.data?.meta?.total_pages || 1;

  const filteredCerts = certificates.filter(
    (c) =>
      c.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Bulk generate certificates mutation
  const bulkGenerateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedExamId || !selectedTemplateId) {
        throw new Error('Please select an exam and template');
      }
      const enrollmentsRes = await examsApi.getEnrolledStudents(selectedExamId);
      const enrollments: any[] = enrollmentsRes.data?.data || [];
      if (enrollments.length === 0) {
        throw new Error('No enrolled students found for this exam');
      }
      const selectedExam = exams.find(e => e.id === selectedExamId);
      const examTitle = selectedExam?.title || 'Exam';
      const recipients = enrollments.map((e: any) => ({
        name: e.student_name || e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim(),
        email: e.student_email || e.email,
        field_values: {
          student_id: e.student_id || e.id,
          exam_id: selectedExamId,
          exam_title: examTitle,
        },
      }));
      return certificatesApi.bulkCreate({
        template_id: selectedTemplateId,
        recipients,
        title: `${examTitle} - Certificate`,
        source_type: 'exam',
        source_id: selectedExamId,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['certchain-certificates', selectedExamId] });
      const data = res.data?.data;
      toast.success(`Generated ${data?.created || 0} certificate(s)`);
      setActiveTab('certificates');
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.response?.data?.message || 'Failed to generate certificates');
    },
  });

  // Revoke certificate mutation
  const revokeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      certificatesApi.revoke(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certchain-certificates', selectedExamId] });
      toast.success('Certificate revoked');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to revoke'),
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: (certId: string) => certificatesApi.sendEmail(certId),
    onSuccess: () => toast.success('Certificate sent via email'),
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to send email'),
  });

  // Download certificate
  const handleDownload = async (certId: string, certCode: string) => {
    try {
      const response = await certificatesApi.download(certId);
      const blob = new Blob([response.data as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certCode}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download certificate');
    }
  };

  const handleExamChange = (examId: string) => {
    setSelectedExamId(examId);
    setSearchTerm('');
    setCertPage(1);
  };

  const eligibleCerts = filteredCerts.filter(
    (c) => c.status === 'issued' || c.status === 'verified'
  );

  const handleSendAllEmails = async () => {
    if (eligibleCerts.length === 0) return;
    setSendingAllEmails(true);
    let sent = 0;
    let failed = 0;
    for (const cert of eligibleCerts) {
      try {
        await certificatesApi.sendEmail(cert.id);
        sent++;
      } catch {
        failed++;
      }
    }
    setSendingAllEmails(false);
    toast.success(`Emails sent: ${sent} succeeded, ${failed} failed`);
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'danger' | 'warning' | 'secondary' => {
    switch (status) {
      case 'issued':
      case 'verified':
        return 'success';
      case 'revoked':
      case 'expired':
        return 'danger';
      case 'pending':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getBlockchainBadgeVariant = (status: string): 'success' | 'danger' | 'warning' | 'secondary' => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const examSuggestions = exams
    .filter((exam) => exam.title.toLowerCase().includes(examSearchTerm.toLowerCase()))
    .map((exam) => ({ id: exam.id, label: exam.title }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Certificate Management</h1>
          <p className="text-white/50 mt-1">Issue, manage and track certificates via CertChain</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['certchain-stats'] });
            queryClient.invalidateQueries({ queryKey: ['certchain-certificates'] });
          }}
        >
          Refresh
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard
          title="Total Issued"
          value={certStats.total ?? 0}
          icon={<Award className="w-4 h-4" />}
          color="teal"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="This Month"
          value={certStats.issued_this_month ?? 0}
          icon={<TrendingUp className="w-4 h-4" />}
          color="green"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Today"
          value={certStats.issued_today ?? 0}
          icon={<Clock className="w-4 h-4" />}
          color="blue"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Verified"
          value={certStats.verified ?? 0}
          icon={<ShieldCheck className="w-4 h-4" />}
          color="purple"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Blockchain"
          value={certStats.blockchain_verified ?? 0}
          icon={<Link2 className="w-4 h-4" />}
          color="orange"
          className="!p-3 [&_h3]:!text-xl"
        />
        <StatCard
          title="Total Views"
          value={certStats.total_views ?? 0}
          icon={<Eye className="w-4 h-4" />}
          color="blue"
          className="!p-3 [&_h3]:!text-xl"
        />
      </div>

      {/* Source Type Breakdown */}
      {certStats.by_source_type && (
        <Card>
          <div className="p-4">
            <h3 className="text-sm font-medium text-white/50 mb-3">Certificates by Source</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(certStats.by_source_type as Record<string, number>).map(([source, count]) => (
                <div key={source} className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.03] border border-white/[0.06] rounded-lg">
                  <span className="text-xs text-white/50 capitalize">{source.replace('_', ' ')}</span>
                  <span className="text-sm font-semibold text-white/90">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Exam Selector + Tabs */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Select Exam</label>
              <SearchInput
                placeholder="Search exams..."
                value={examSearchTerm}
                onChange={(value) => setExamSearchTerm(value)}
                suggestions={examSuggestions}
                onSuggestionSelect={(suggestion) => {
                  handleExamChange(String(suggestion.id));
                  setExamSearchTerm(suggestion.label);
                }}
                showClear
                size="md"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-white/40 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                {selectedExamId
                  ? `Viewing certificates for: ${exams.find(e => e.id === selectedExamId)?.title || 'Selected exam'}`
                  : 'Select an exam to view or generate certificates'}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 border-b border-white/[0.06]">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'templates'
                  ? 'border-teal-500 text-teal-400'
                  : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
              }`}
            >
              <Layout className="w-4 h-4 inline mr-2" />
              Templates & Generate
            </button>
            <button
              onClick={() => setActiveTab('certificates')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'certificates'
                  ? 'border-teal-500 text-teal-400'
                  : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
              }`}
            >
              <Award className="w-4 h-4 inline mr-2" />
              Certificates
              {certificates.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/[0.08] rounded-full">{certificates.length}</span>
              )}
            </button>
          </nav>
        </div>

        {/* Templates Tab Content */}
        {activeTab === 'templates' && (
          <div className="p-6 space-y-6">
            {/* Template Selection */}
            <div>
              <h3 className="text-lg font-semibold text-white/90 mb-1">Select Certificate Template</h3>
              <p className="text-sm text-white/40 mb-4">Choose an exam certificate template from CertChain</p>

              {templatesLoading ? (
                <div className="py-12 text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-white/60">Loading templates from CertChain...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="py-12 text-center">
                  <Layout className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60">No templates available</p>
                  <p className="text-xs text-white/40 mt-1">Create templates in the CertChain service first</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplateId(template.id)}
                      className={`p-4 border-2 rounded-xl text-left transition-all group ${
                        selectedTemplateId === template.id
                          ? 'border-teal-500 bg-teal-500/10 shadow-[0_0_15px_rgba(20,184,166,0.15)]'
                          : 'border-white/[0.06] hover:border-white/[0.12] bg-white/[0.02]'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedTemplateId === template.id
                              ? 'bg-teal-500/20'
                              : 'bg-white/[0.06] group-hover:bg-white/[0.08]'
                          }`}>
                            <FileCheck className={`w-5 h-5 ${
                              selectedTemplateId === template.id ? 'text-teal-400' : 'text-white/50'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white/90 text-sm">{template.name}</h4>
                            {template.description && (
                              <p className="text-xs text-white/40 mt-0.5 line-clamp-1">{template.description}</p>
                            )}
                          </div>
                        </div>
                        {selectedTemplateId === template.id && (
                          <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0" />
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-3 text-xs">
                        <Badge variant="secondary" size="sm">
                          {template.type_display || template.type}
                        </Badge>
                        <span className="text-white/30">v{template.version}</span>
                        {template.is_default && (
                          <Badge variant="success" size="sm">Default</Badge>
                        )}
                        {template.usage_count > 0 && (
                          <span className="text-white/30">{template.usage_count} issued</span>
                        )}
                      </div>

                      {template.preview_url && (
                        <div className="mt-3">
                          <img
                            src={template.preview_url}
                            alt={template.name}
                            className="w-full h-20 object-cover rounded-lg opacity-50 group-hover:opacity-70 transition-opacity"
                          />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Generate Action */}
            <div className="border-t border-white/[0.06] pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-white/90">Generate Certificates</h3>
                  <p className="text-sm text-white/40">
                    Bulk-generate certificates for all enrolled students of the selected exam.
                  </p>
                  <div className="flex items-center gap-4 text-sm mt-2">
                    {selectedExamId ? (
                      <span className="text-white/60">
                        Exam: <strong className="text-white/80">{exams.find(e => e.id === selectedExamId)?.title}</strong>
                      </span>
                    ) : (
                      <span className="text-amber-400/80 text-xs">Select an exam above</span>
                    )}
                    {selectedTemplateId ? (
                      <span className="text-white/60">
                        Template: <strong className="text-white/80">{templates.find(t => t.id === selectedTemplateId)?.name}</strong>
                      </span>
                    ) : selectedExamId ? (
                      <span className="text-amber-400/80 text-xs">Select a template</span>
                    ) : null}
                  </div>
                </div>
                <Button
                  variant="primary"
                  leftIcon={<RefreshCw className={`w-4 h-4 ${bulkGenerateMutation.isPending ? 'animate-spin' : ''}`} />}
                  onClick={() => bulkGenerateMutation.mutate()}
                  disabled={!selectedExamId || !selectedTemplateId || bulkGenerateMutation.isPending}
                >
                  {bulkGenerateMutation.isPending ? 'Generating...' : 'Generate Certificates'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Certificates Tab Content */}
        {activeTab === 'certificates' && (
          <div>
            {/* Search & Actions Bar */}
            <div className="p-4 border-b border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Search by name or code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-72"
                  />
                </div>
                <div className="flex items-center gap-3">
                  {filteredCerts.length > 0 && (
                    <span className="text-xs text-white/40">
                      {filteredCerts.length} certificate{filteredCerts.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {eligibleCerts.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={sendingAllEmails ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      onClick={handleSendAllEmails}
                      disabled={sendingAllEmails}
                    >
                      {sendingAllEmails ? 'Sending...' : `Email All (${eligibleCerts.length})`}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            {!selectedExamId ? (
              <div className="py-16 text-center">
                <Award className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">Select an exam to view certificates</p>
                <p className="text-xs text-white/40 mt-1">Use the dropdown above to choose an exam</p>
              </div>
            ) : certsLoading ? (
              <div className="py-16 text-center">
                <div className="spinner mx-auto mb-4"></div>
                <p className="text-white/60">Loading certificates...</p>
              </div>
            ) : filteredCerts.length === 0 ? (
              <div className="py-16 text-center">
                <Award className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No certificates found</p>
                <p className="text-xs text-white/40 mt-1">
                  Switch to the Templates tab to generate certificates for this exam
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Recipient</TableHeader>
                      <TableHeader>Code</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Title</TableHeader>
                      <TableHeader>Blockchain</TableHeader>
                      <TableHeader>Issued</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCerts.map((cert) => (
                      <TableRow key={cert.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-white">{cert.recipient_name || '—'}</div>
                            {cert.recipient_email && (
                              <div className="text-sm text-white/50">{cert.recipient_email}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-white/70 bg-white/[0.04] px-2 py-1 rounded">
                            {cert.code}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(cert.status)} size="sm">
                            {cert.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white/60 max-w-[200px] truncate">
                          {cert.title}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant={getBlockchainBadgeVariant(cert.blockchain_status)} size="sm">
                              {cert.blockchain_status || 'none'}
                            </Badge>
                            {cert.transaction_hash && (
                              <span className="text-[10px] text-white/30 font-mono truncate max-w-[100px]" title={cert.transaction_hash}>
                                {cert.transaction_hash.slice(0, 10)}...
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-white/50 text-sm">
                          {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {(cert.status === 'issued' || cert.status === 'verified') && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leftIcon={<Download className="w-3 h-3" />}
                                  onClick={() => handleDownload(cert.id, cert.code)}
                                  title="Download PDF"
                                >
                                  PDF
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leftIcon={<Mail className="w-3 h-3" />}
                                  onClick={() => sendEmailMutation.mutate(cert.id)}
                                  disabled={sendEmailMutation.isPending}
                                  title="Send via email"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  leftIcon={<XCircle className="w-3 h-3" />}
                                  onClick={() => revokeMutation.mutate({ id: cert.id, reason: 'Revoked by teacher' })}
                                  disabled={revokeMutation.isPending}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  title="Revoke"
                                />
                              </>
                            )}
                            {cert.status === 'revoked' && (
                              <span className="text-xs text-red-400/60 px-2">Revoked</span>
                            )}
                            {cert.status === 'pending' && (
                              <span className="text-xs text-amber-400/60 px-2">Pending</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {certTotalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-white/[0.06]">
                    <p className="text-sm text-white/50">
                      Page {certPage} of {certTotalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ChevronLeft className="w-4 h-4" />}
                        onClick={() => setCertPage((p) => Math.max(1, p - 1))}
                        disabled={certPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCertPage((p) => Math.min(certTotalPages, p + 1))}
                        disabled={certPage === certTotalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
